import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';

export const PAAC_TAX_MODE = {
  TAX_EXCLUSIVE: 'tax_exclusive',
  TAX_INCLUSIVE: 'tax_inclusive',
} as const;

export const PAAC_LINKABLE_STATUS = {
  APPROVED_ONLY: 'approved_only',
  APPROVED_AND_PENDING: 'approved_and_pending',
} as const;

type PaacTaxMode = (typeof PAAC_TAX_MODE)[keyof typeof PAAC_TAX_MODE];
type PaacLinkableStatus =
  (typeof PAAC_LINKABLE_STATUS)[keyof typeof PAAC_LINKABLE_STATUS];

export type PaacDetailFormValues = {
  taxMode: PaacTaxMode;
  linkableStatus: PaacLinkableStatus;
};

type PaacSettingState = PaacDetailFormValues & {
  isEnabled: boolean;
};

const INITIAL_PAAC_SETTING: PaacSettingState = {
  isEnabled: false,
  taxMode: PAAC_TAX_MODE.TAX_EXCLUSIVE,
  linkableStatus: PAAC_LINKABLE_STATUS.APPROVED_ONLY,
};

export const usePaacSetting = () => {
  const [setting, setSetting] = useState(INITIAL_PAAC_SETTING);
  const [isEditingDetail, setIsEditingDetail] = useState(false);
  const formMethods = useForm<PaacDetailFormValues>({
    defaultValues: {
      taxMode: INITIAL_PAAC_SETTING.taxMode,
      linkableStatus: INITIAL_PAAC_SETTING.linkableStatus,
    },
  });

  const startUsing = useCallback(() => {
    setSetting((prev) => ({ ...prev, isEnabled: true }));
  }, []);

  const stopUsing = useCallback(() => {
    setSetting((prev) => ({ ...prev, isEnabled: false }));
    setIsEditingDetail(false);
  }, []);

  const startEditingDetail = useCallback(() => {
    setIsEditingDetail(true);
  }, []);

  const cancelEditingDetail = useCallback(() => {
    formMethods.reset({
      taxMode: setting.taxMode,
      linkableStatus: setting.linkableStatus,
    });
    setIsEditingDetail(false);
  }, [formMethods, setting.taxMode, setting.linkableStatus]);

  const saveDetail = useCallback((values: PaacDetailFormValues) => {
    setSetting((prev) => ({ ...prev, ...values }));
    setIsEditingDetail(false);
  }, []);

  return {
    ...setting,
    isEditingDetail,
    formMethods,
    startUsing,
    stopUsing,
    startEditingDetail,
    cancelEditingDetail,
    saveDetail,
  };
};
